"use client";

import useSearchRouter from "@/app/hooks/useSearchRouter";
import Checkbox from "@/components/design-system/checkbox";
import { setComplete } from "@/store/clearRefinementSlice";
import { AppState } from "@/store/store";
import { useParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import style from "./genericFacet.module.scss";

export default function GenericFacetValue({
  parentFacet,
  value,
  count,
  searchTerm,
  handleChange,
  unselectTrigger,
}: GenericFacetValueProps) {
  const id = `${parentFacet}-${value}`;
  const params = useParams();
  const { isFacetActive } = useSearchRouter();

  const [isChecked, setIsChecked] = useState<boolean | undefined>(isFacetActive(parentFacet, value));
  const refinementToClear = useSelector((state: AppState) => state.clearRefinementSlice.refinementToRemove);
  const dispatch = useDispatch();

  useEffect(() => {
    if (refinementToClear && refinementToClear === value) {
      setIsChecked(false);
      dispatch(setComplete());
    }
  }, [dispatch, refinementToClear, value]);

  useEffect(() => {
    setIsChecked(isFacetActive(parentFacet, value));
  }, [isFacetActive, params, parentFacet, value]);

  useEffect(() => {
    if (unselectTrigger > 0) {
      setIsChecked(false);
    }
  }, [unselectTrigger]);

  const getFacetText = (text: string, highlight: string) => {
    if (highlight !== "") {
      const parts = text.split(new RegExp(`(${highlight})`, "gi"));
      return (
        <span>
          {parts.map((part, i) => (
            <span key={i} className={part.toLowerCase() === highlight.toLowerCase() ? style.highlightText : ""}>
              {part}
            </span>
          ))}
        </span>
      );
    }
    return text;
  };

  const toggleChecked = useCallback(
    (event?: React.SyntheticEvent) => {
      setIsChecked(!isChecked);
      if (event) {
        handleChange?.(event, value);
      }
    },
    [isChecked, handleChange, value],
  );

  return (
    <li className={style.facetValue}>
      <div className={style.filterRow}>
        <Checkbox
          checked={isChecked}
          onChange={(event) => toggleChecked(event)}
          id={id}
          name={value}
          showLabel={false}
          aria-label={value}
        />
        <label htmlFor={id} className={`${style.facetNameText} ${isChecked ? style.selected : ""}`} title={value}>
          {getFacetText(value, searchTerm)}
        </label>
        <span className={style.count}>{count}</span>
      </div>
    </li>
  );
}

export type GenericFacetValueProps = {
  parentFacet: string;
  value: string;
  count: number;
  searchTerm: string;
  handleChange?: (event: React.SyntheticEvent, value: string) => void;
  unselectTrigger: number;
};
